import React, { useState } from "react"
import { Navigation } from "@kit/design/navigation"
import { TrialIndicator } from "@kit/design/trial-indicator"
import { ProgressCircle } from "@kit/design/progress-circle"
import { CountBadge } from "@kit/design/count-badge"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import mount from "core/mount"
import { Link } from "core/link"
import { useCurrentUser } from "hooks/use-current-user"
import { StatusBanner } from "./utils/status-banner"
import { UserAccountMenu } from "./user-account-menu"
import { BulkNotificationsDrawer } from "app/bulk-notifications"
import { useBulkNotifications } from "app/bulk-notifications/use-bulk-notifications"
import { DiscoveryHub } from "app/discovery-hub"

function NavigationFooter({
  user,
  onUpgrade,
  renderLink,
  onWhatsNewClick,
  whatsNewCount,
  notificationsLeading,
  unseenCount,
  onNotificationsClick
}) {
  return (
    <Navigation.Footer>
      {user.trialing && (
        <Navigation.FooterCallout>
          <TrialIndicator
            daysRemaining={user.trial_days_remaining ?? 0}
            daysTotal={user.trial_days_total ?? 0}
            onUpgrade={onUpgrade}
          />
        </Navigation.FooterCallout>
      )}
      <Navigation.Menu>
        {notificationsLeading && (
          <Navigation.Item
            leading={notificationsLeading}
            label="Notifications"
            trailing={
              unseenCount > 0 ? <CountBadge count={unseenCount} /> : null
            }
            render={<button type="button" onClick={onNotificationsClick} />}
          />
        )}
        <Navigation.Item
          leading="settings-cog"
          label="Settings"
          pathPrefix="/account_settings"
          render={renderLink("/account_settings")}
        />
      </Navigation.Menu>
      <UserAccountMenu
        user={user}
        onWhatsNewClick={onWhatsNewClick}
        whatsNewCount={whatsNewCount}
      />
    </Navigation.Footer>
  )
}

export function AppNavigation({
  currentPath: currentPathProp,
  renderLink = href => <Link href={href} />,
  onCollapsedChange
} = {}) {
  const user = useCurrentUser()
  const currentPath = currentPathProp ?? window.location.pathname
  const nav = path => ({ pathPrefix: path, render: renderLink(path) })
  const [discoveryHubOpen, setDiscoveryHubOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [whatsNewCount, setWhatsNewCount] = useState(0)
  const { topics, activeTopic, progress, unseenCount, hasTopics, markSeen } =
    useBulkNotifications()
  const notificationsLeading = hasTopics ? (
    activeTopic ? (
      <ProgressCircle progress={progress} />
    ) : (
      "alarm"
    )
  ) : null

  const handleNotificationsOpen = () => {
    setNotificationsOpen(true)
    markSeen()
  }
  const isMobile = !useMediaBreakpoint("md")
  const accountType =
    user.access_level?.pro ||
    user.access_level?.standard ||
    user.access_level?.newsletter_paid
      ? "paid"
      : "free"

  return (
    <Navigation
      currentPath={currentPath}
      mobile={isMobile}
      accountType={accountType}
      onCollapsedChange={onCollapsedChange}
    >
      <Navigation.Sidebar>
        <Navigation.Header>
          <Navigation.HeaderCollapsibleContent>
            <Navigation.LogoLink
              pathPrefix="/dashboard"
              {...nav("/dashboard")}
            />
            <StatusBanner />
          </Navigation.HeaderCollapsibleContent>
          <Navigation.Trigger />
        </Navigation.Header>

        <Navigation.PrimaryAction
          icon="pencil-write"
          label="Start writing"
          render={<Link href="/campaigns/new/steps" method="post" />}
        />

        <Navigation.Menu>
          <Navigation.Item
            leading="home"
            label="Dashboard"
            {...nav("/dashboard")}
          />
          <Navigation.Item
            leading="three-people"
            label="Subscribers"
            tooltip="Browse all your subscribers and customers"
            {...nav("/subscribers")}
          />
          <Navigation.Item
            leading="megaphone"
            label="Broadcasts"
            tooltip="Send an email to some, or all, of your audience, and to your public feed"
            {...nav("/campaigns")}
          />

          <Navigation.Group icon="trending-up" label="Audience growth">
            <Navigation.GroupItem
              label="Landing Pages & Forms"
              tooltip="Gather subscribers with a hosted landing page or a form embedded on your website"
              {...nav("/forms")}
            />
            <Navigation.GroupItem
              label="Recommendations"
              tooltip="Join the Creator Network to cross-promote your work with other creators and grow your email lists together"
              {...nav("/creator-network")}
            />
            <Navigation.GroupItem
              label="Newsletter Site"
              tooltip="Your hub on the internet. A one-stop-shop for your subscribers to find your content & products"
              {...nav("/creator_profile")}
            />
          </Navigation.Group>

          <Navigation.Group icon="money" label="Monetization">
            <Navigation.GroupItem
              label="Products"
              tooltip="Sell digital products and content subscriptions"
              {...nav("/products")}
            />
            {user.canViewAds && (
              <Navigation.GroupItem
                label="Sponsorships"
                badge={accountType === "paid" ? "beta" : "paid"}
                tooltip="Monetize your content with sponsorships and advertising"
                {...nav("/ads")}
              />
            )}
            {user.canUsePaidRecommendations && (
              <Navigation.GroupItem
                label="Paid Recommendations"
                badge="paid"
                tooltip="Get paid to recommend other creators in the Creator Network"
                {...nav("/paid_recommendations")}
              />
            )}
            <Navigation.GroupItem
              label="Tips"
              tooltip="Give your audience a simple way to support your work"
              {...nav("/tips")}
            />
            <Navigation.GroupItem
              label="Payouts"
              tooltip="See a breakdown of all payouts you've received"
              {...nav("/account_settings/commerce")}
            />
          </Navigation.Group>

          <Navigation.Group icon="bolt" label="Automation" badge="paid">
            <Navigation.GroupItem
              label="Visual Automations"
              tooltip="Create powerful, automated funnels for your audience"
              {...nav("/automations")}
            />
            <Navigation.GroupItem
              label="Rules"
              tooltip={'"If this, then that" automation rules'}
              {...nav("/rules")}
            />
            <Navigation.GroupItem
              label="Email Sequences"
              tooltip="Create multiple emails that are sent in order"
              {...nav("/sequences")}
            />
            <Navigation.GroupItem
              label="Kit MCP"
              badge="beta"
              tooltip="Connect your Kit account to your favorite AI tool"
              {...nav("/kit-mcp")}
            />
            <Navigation.GroupItem
              label="RSS"
              tooltip="Generate email or digests when RSS feeds update"
              {...nav("/feeds")}
            />
          </Navigation.Group>

          {user.accountCanAccessApps && (
            <Navigation.Item
              leading="app-store"
              label="Apps"
              badge="paid"
              tooltip="Power up your account by connecting with your favorite apps and integrations"
              {...nav("/apps")}
            />
          )}
        </Navigation.Menu>

        <NavigationFooter
          user={user}
          onUpgrade={() => {
            window.location.href = "/account/upgrade"
          }}
          renderLink={renderLink}
          onWhatsNewClick={() => setDiscoveryHubOpen(true)}
          whatsNewCount={whatsNewCount}
          notificationsLeading={notificationsLeading}
          unseenCount={unseenCount}
          onNotificationsClick={handleNotificationsOpen}
        />
      </Navigation.Sidebar>

      <Navigation.Dock>
        <Navigation.Dock.Trigger />
        <Navigation.Dock.Actions>
          <Navigation.Dock.Action
            label="Dashboard"
            icon="home"
            {...nav("/dashboard")}
          />
          <Navigation.Dock.Action
            label="Subscribers"
            icon="three-people"
            {...nav("/subscribers")}
          />
          <Navigation.Dock.Action
            label="Broadcasts"
            icon="megaphone"
            {...nav("/campaigns")}
          />
        </Navigation.Dock.Actions>
        <Navigation.Dock.PrimaryAction
          label="Start writing"
          icon="pencil-write"
          render={<Link href="/campaigns/new/steps" />}
        />
      </Navigation.Dock>

      <BulkNotificationsDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        topics={topics}
      />

      <DiscoveryHub
        open={discoveryHubOpen}
        onClose={() => setDiscoveryHubOpen(false)}
        onNotificationCount={setWhatsNewCount}
      />
    </Navigation>
  )
}

mount({ AppNavigation })
